import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  Typography,
  Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  useCreateMaterialMutation,
  useUploadMaterialImageMutation,
} from '../../redux/api/materialsApi';
import MaterialAutocomplete from '../MaterialAutocomplete';
import { toast } from 'react-toastify';

const { Text } = Typography;
const { TextArea } = Input;

const EMPTY_FORM = {
  name: '',
  code: '',
  color: '',
  type: '',
  style: '',
  category: '',
  description: '',
};

/**
 * Create a material. Default UI is minimal (name + optional color).
 * Pass simple={false} for the full form.
 */
const MaterialCreationModal = ({
  show,
  onHide,
  onMaterialCreated,
  simple = true,
}) => {
  const [form] = Form.useForm();
  const [createMaterial, { isLoading: loading }] = useCreateMaterialMutation();
  const [uploadMaterialImage] = useUploadMaterialImageMutation();
  const [image, setImage] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [showTemplateSearch, setShowTemplateSearch] = useState(false);

  useEffect(() => {
    if (show) {
      form.setFieldsValue(EMPTY_FORM);
      setImage(null);
      setShowMore(false);
      setShowTemplateSearch(false);
    }
  }, [show, form]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setImage(null);
    setShowMore(false);
    setShowTemplateSearch(false);
    onHide();
  }, [form, onHide]);

  const handleTemplateSelect = (material) => {
    form.setFieldsValue({
      name: `${material.name || ''} (Copy)`.trim(),
      code: material.code || '',
      color: material.color || '',
      type: material.type || '',
      style: material.style || '',
      category: material.category || '',
      description: material.description || '',
    });
    setShowTemplateSearch(false);
    setShowMore(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name.trim(),
        code: values.code?.trim() || '',
        color: values.color?.trim() || '',
        type: values.type?.trim() || '',
        style: values.style?.trim() || '',
        category: values.category?.trim() || '',
        description: values.description?.trim() || '',
      };

      const material = await createMaterial(payload).unwrap();

      if (image && material.id) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('image', image);
          imageFormData.append('material_id', material.id);
          imageFormData.append('title', image.name);
          imageFormData.append('description', '');
          await uploadMaterialImage({
            materialId: material.id,
            formData: imageFormData,
          }).unwrap();
        } catch (imageError) {
          console.error('Failed to upload material image:', imageError);
          toast.warning('Material created but image upload failed');
        }
      }

      toast.success('Material created');
      onMaterialCreated(material);
      handleCancel();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(
        err?.data?.message || err?.error || 'Failed to create material'
      );
    }
  };

  const moreFields = (
    <>
      <Form.Item name="code" label="Code">
        <Input placeholder="Optional supplier / stock code" />
      </Form.Item>
      <Form.Item name="type" label="Type">
        <Input placeholder="e.g. Fabric, Leather, Metal" />
      </Form.Item>
      <Form.Item name="style" label="Style">
        <Input placeholder="Optional" />
      </Form.Item>
      <Form.Item name="category" label="Category">
        <Input placeholder="Optional" />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <TextArea rows={2} placeholder="Optional" maxLength={500} showCount />
      </Form.Item>
      <Form.Item label="Image">
        <Upload
          beforeUpload={(file) => {
            setImage(file);
            return false;
          }}
          onRemove={() => setImage(null)}
          maxCount={1}
          accept="image/*"
          listType="text"
        >
          <Button icon={<UploadOutlined />}>Upload image</Button>
        </Upload>
      </Form.Item>
      <div style={{ marginBottom: 8 }}>
        <Button
          type="link"
          size="small"
          style={{ paddingInline: 0 }}
          onClick={() => setShowTemplateSearch((v) => !v)}
        >
          {showTemplateSearch
            ? 'Hide template search'
            : 'Copy from existing material'}
        </Button>
        {showTemplateSearch ? (
          <div style={{ marginTop: 8 }}>
            <MaterialAutocomplete
              onSelect={handleTemplateSelect}
              placeholder="Search materials to copy..."
            />
          </div>
        ) : null}
      </div>
    </>
  );

  return (
    <Modal
      title="Add material"
      open={!!show}
      onCancel={handleCancel}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={handleSubmit}
        >
          Add material
        </Button>,
      ]}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Start with a name — you can add more details later if needed.
      </Text>
      <Form
        form={form}
        layout="vertical"
        initialValues={EMPTY_FORM}
        requiredMark="optional"
      >
        <Form.Item
          name="name"
          label="Material name"
          rules={[
            { required: true, message: 'Enter a material name' },
            { whitespace: true, message: 'Enter a material name' },
          ]}
          required
        >
          <Input
            placeholder="e.g. Alcantara black, Oak veneer"
            autoFocus
            maxLength={120}
          />
        </Form.Item>

        <Form.Item name="color" label="Color">
          <Input placeholder="Optional, e.g. Black, Beige" maxLength={60} />
        </Form.Item>

        {simple ? (
          <Collapse
            ghost
            size="small"
            activeKey={showMore ? ['more'] : []}
            onChange={(keys) => setShowMore(keys.includes('more'))}
            items={[
              {
                key: 'more',
                label: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    More options (code, type, image…)
                  </Text>
                ),
                children: moreFields,
              },
            ]}
          />
        ) : (
          moreFields
        )}
      </Form>
    </Modal>
  );
};

export default MaterialCreationModal;
